import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { getList, type List, type ListItem } from "../../api/lists";
import { ReadingListDetailPane } from "../../components/lists/ReadingListDetailPane";

// Query key for the detail-pane fetch. Exported so the edit/picker/row
// mutations can invalidate the cache without duplicating the literal.
export const readingListDetailQueryKey = (listId: number | null | undefined) =>
  ["reading-list", listId] as const;

// Mounted at TWO routes:
//   - /reading-lists/:listId             (top-level, public, no shelf chrome)
//   - /my-shelf/reading-lists/:listId    (under MyShelfScreen, tabs visible)
//
// Same screen, same data fetch — the only difference is whether the parent
// renders the My Shelf tab bar. We don't branch on the URL ourselves; React
// Router decides which outlet picks us up.
export default function ReadingListDetailScreen() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { listId: listIdParam } = useParams<{ listId: string }>();
  const listId = listIdParam ? Number(listIdParam) : null;

  const query = useQuery({
    queryKey: readingListDetailQueryKey(listId),
    queryFn: () => {
      if (listId == null || Number.isNaN(listId)) {
        return Promise.reject(new Error("invalid list id"));
      }
      return getList(listId);
    },
    enabled: listId != null && !Number.isNaN(listId),
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <Flex justify="center" py="48px">
        <Spinner color="dune.dune" />
      </Flex>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Flex direction="column" align="center" gap="8px" py="48px" px="16px">
        <Text fontSize="14px" color="error.300">
          {t("screens.shelf.readingLists.error.title")}
        </Text>
        <Text fontSize="12px" color="neutral.400">
          {t("screens.shelf.readingLists.error.retry")}
        </Text>
      </Flex>
    );
  }

  // Detail endpoint ships items under the legacy `items` key (PR #330
  // shape — mobile clients still read it). Rename to `list_items` at
  // this boundary so every downstream component (pane, picker, item
  // row, edit hooks) can stay on the canonical name that the listing
  // endpoints also use. Cheaper than dual-shipping both keys on the
  // wire.
  const raw = query.data.data.payload.results as List & {
    items?: ListItem[];
  };
  // Prefer `items` over `list_items` because the detail endpoint's
  // ListEntity ships an empty `list_items: []` field through
  // getJSONData (entity virtual field, not populated for this code
  // path) alongside the explicit `items` key that handle_get sets.
  // The older `raw.list_items ?? raw.items` shape was picking up the
  // empty array first and never falling through to the real data.
  const items = raw.items ?? raw.list_items ?? [];
  // Derive `list_type` from `list_type_id`. The backend only ships the
  // numeric id (2 = comics, 4 = releases per the lists_types table); the
  // pane keys off the string discriminator everywhere ("Series in list" vs
  // "Releases in list", "series" vs "releases" stat label, comic vs
  // release row rendering). Without this derivation the discriminator is
  // always undefined → every list renders as comics regardless of type.
  const list: List = {
    ...raw,
    list_items: items,
    list_type:
      raw.list_type ?? (raw.list_type_id === 4 ? "releases" : "comics"),
  };

  return (
    <ReadingListDetailPane
      list={list}
      // The standalone screen has no rail next to it, so the back button
      // is the only way back. Always render it; the pane's own back button
      // gating was for the master/detail mobile case which doesn't apply.
      showBackButton
      onBack={() => navigate("/my-shelf/reading-lists")}
    />
  );
}
